import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";
import { useState } from "react";
import { z } from "zod";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApprovalTable } from "@/components/supervisor/approvals/ApprovalTable";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { useFetchApprovalRequests } from "@/hooks/api/approvals/useFetchApprovalRequests";
import { APPROVAL_STATUS_LABELS, type ApprovalStatus } from "@/types/approval";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";

const STATUSES: ApprovalStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
];

export const Route = createFileRoute("/supervisor/_layout/requests")({
  beforeLoad: () => requireFeature("leave"),
  validateSearch: z.object({
    status: z
      .enum(["ALL", ...STATUSES])
      .catch("ALL")
      .default("ALL"),
  }),
  component: RequestsPage,
});

function RequestsPage() {
  const navigate = useNavigate();
  const { status } = Route.useSearch();
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);

  const requestsQuery = useFetchApprovalRequests({
    page,
    page_size: pageSize,
    // Approver-facing list: your own leave lives under My Requests.
    exclude_self: true,
    // axios drops undefined params, so "ALL" means no status filter.
    status: status === "ALL" ? undefined : status,
  });
  const requests = requestsQuery.data?.results ?? [];
  const count = requestsQuery.data?.count ?? 0;
  const loading = requestsQuery.isPending;
  const isFetching = requestsQuery.isFetching;

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="All Leave Requests"
          description="Approval history for requests routed to you."
          onToggleFilter={() => setShowFilters((p) => !p)}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => {
            navigate({
              to: ".",
              search: (prev) => ({ ...prev, status: "ALL" }),
              replace: true,
            });
            setPage(1);
          }}
        >
          <Select
            value={status}
            onValueChange={(v) => {
              // Resolve the Radix string back to a known status; the only
              // non-status option rendered is "ALL".
              const next = STATUSES.find((s) => s === v) ?? "ALL";
              navigate({
                to: ".",
                search: (prev) => ({ ...prev, status: next }),
                replace: true,
              });
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {APPROVAL_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FiltersPanel>

        <ApprovalTable
          requests={requests}
          loading={loading}
          onViewDetails={(request) =>
            navigate({
              to: "/supervisor/requests/$id",
              params: { id: String(request.id) },
            })
          }
          emptyMessage="No requests match this filter."
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </SidebarInset>
  );
}
